/**
 * Surface nets — 스칼라 필드(0.5 등고면)에서 사각형 메시를 뽑는다.
 *
 * 마칭 큐브 대신 이걸 쓰는 이유: 256×16 짜리 표가 필요 없고, 결과가 사각형 격자라
 * 라플라시안 스무딩이 자연스럽고, 가장자리가 전부 바깥(<0.5)인 필드에서는
 * 닫힌 다양체가 나온다. 비주얼 헐과 (앞으로) 복셀 조각 모두 이걸 먹는다.
 */

import { clamp } from '../imaging/raster.ts';

export interface Grid3 {
  nx: number;
  ny: number;
  nz: number;
  data: Float32Array;
}

export const gridIndex = (g: Grid3, i: number, j: number, k: number) => (k * g.ny + j) * g.nx + i;

/** 3D 박스 블러 — 축 분리. 복셀 계단을 죽인다. */
export function blur3(g: Grid3, r: number): Grid3 {
  if (r < 1) return g;
  const { nx, ny, nz } = g;
  let src: Float32Array = g.data;
  let dst: Float32Array = new Float32Array(src.length);
  const norm = 1 / (2 * r + 1);
  const idx = (i: number, j: number, k: number) => (k * ny + j) * nx + i;

  for (let k = 0; k < nz; k++) for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
    let s = 0;
    for (let d = -r; d <= r; d++) s += src[idx(clamp(i + d, 0, nx - 1) | 0, j, k)]!;
    dst[idx(i, j, k)] = s * norm;
  }
  [src, dst] = [dst, src];
  for (let k = 0; k < nz; k++) for (let i = 0; i < nx; i++) for (let j = 0; j < ny; j++) {
    let s = 0;
    for (let d = -r; d <= r; d++) s += src[idx(i, clamp(j + d, 0, ny - 1) | 0, k)]!;
    dst[idx(i, j, k)] = s * norm;
  }
  [src, dst] = [dst, src];
  for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) for (let k = 0; k < nz; k++) {
    let s = 0;
    for (let d = -r; d <= r; d++) s += src[idx(i, j, clamp(k + d, 0, nz - 1) | 0)]!;
    dst[idx(i, j, k)] = s * norm;
  }
  return { nx, ny, nz, data: dst };
}

const CORNER = [
  [0, 0, 0], [1, 0, 0], [0, 1, 0], [1, 1, 0],
  [0, 0, 1], [1, 0, 1], [0, 1, 1], [1, 1, 1],
] as const;
const EDGE = [
  [0, 1], [2, 3], [4, 5], [6, 7],
  [0, 2], [1, 3], [4, 6], [5, 7],
  [0, 4], [1, 5], [2, 6], [3, 7],
] as const;

export interface NetsResult {
  /** 격자 좌표(셀 단위, 실수)의 정점 */
  positions: number[];
  /** 사각형 4개씩. 감기 방향은 바깥을 향한다. */
  quads: number[];
}

/**
 * 격자 노드 필드 → 사각형 메시. 정점 좌표는 **격자 인덱스 공간**(i,j,k 실수)이다.
 * 호출부가 원하는 월드로 옮긴다.
 */
export function surfaceNets(g: Grid3, iso = 0.5): NetsResult {
  const { nx, ny, nz, data } = g;
  const inside = (v: number) => v >= iso;
  const idx = (i: number, j: number, k: number) => (k * ny + j) * nx + i;
  const cidx = (i: number, j: number, k: number) => (k * (ny - 1) + j) * (nx - 1) + i;
  const cellVertex = new Int32Array((nx - 1) * (ny - 1) * (nz - 1)).fill(-1);
  const positions: number[] = [];
  const vals = new Array<number>(8);

  for (let k = 0; k < nz - 1; k++) {
    for (let j = 0; j < ny - 1; j++) {
      for (let i = 0; i < nx - 1; i++) {
        let mask = 0;
        for (let c = 0; c < 8; c++) {
          const [dx, dy, dz] = CORNER[c]!;
          const v = data[idx(i + dx, j + dy, k + dz)]!;
          vals[c] = v;
          if (inside(v)) mask |= 1 << c;
        }
        if (mask === 0 || mask === 0xff) continue;
        let sx = 0, sy = 0, sz = 0, n = 0;
        for (const [a, b] of EDGE) {
          const va = vals[a]!, vb = vals[b]!;
          if (inside(va) === inside(vb)) continue;
          const t = (iso - va) / (vb - va || 1e-6);
          const ca = CORNER[a]!, cb = CORNER[b]!;
          sx += ca[0] + (cb[0] - ca[0]) * t;
          sy += ca[1] + (cb[1] - ca[1]) * t;
          sz += ca[2] + (cb[2] - ca[2]) * t;
          n++;
        }
        cellVertex[cidx(i, j, k)] = positions.length / 3;
        positions.push(i + sx / n, j + sy / n, k + sz / n);
      }
    }
  }

  const quads: number[] = [];
  const push = (a: number, b: number, c: number, d: number, flip: boolean) => {
    if (a < 0 || b < 0 || c < 0 || d < 0) return;
    if (flip) quads.push(a, d, c, b); else quads.push(a, b, c, d);
  };
  // 뒤집지 않은 순서는 법선이 +축을 향한다. 안쪽 노드가 앞(v0)이면 바깥이 +축이므로 그대로.
  for (let k = 1; k < nz - 1; k++) {
    for (let j = 1; j < ny - 1; j++) {
      for (let i = 1; i < nx - 1; i++) {
        const v0 = inside(data[idx(i, j, k)]!);
        if (inside(data[idx(i + 1, j, k)]!) !== v0) {
          push(cellVertex[cidx(i, j - 1, k - 1)]!, cellVertex[cidx(i, j, k - 1)]!, cellVertex[cidx(i, j, k)]!, cellVertex[cidx(i, j - 1, k)]!, !v0);
        }
        if (inside(data[idx(i, j + 1, k)]!) !== v0) {
          push(cellVertex[cidx(i - 1, j, k - 1)]!, cellVertex[cidx(i - 1, j, k)]!, cellVertex[cidx(i, j, k)]!, cellVertex[cidx(i, j, k - 1)]!, !v0);
        }
        if (inside(data[idx(i, j, k + 1)]!) !== v0) {
          push(cellVertex[cidx(i - 1, j - 1, k)]!, cellVertex[cidx(i, j - 1, k)]!, cellVertex[cidx(i, j, k)]!, cellVertex[cidx(i - 1, j, k)]!, !v0);
        }
      }
    }
  }
  return { positions, quads };
}

/** 사각형 이웃 평균 라플라시안 스무딩. positions 를 제자리에서 바꾼다. */
export function relaxQuadMesh(positions: number[], quads: number[], iterations: number, keep = 0.4): void {
  const vcount = positions.length / 3;
  if (iterations <= 0 || vcount === 0) return;
  const neighbors: number[][] = Array.from({ length: vcount }, () => []);
  for (let q = 0; q < quads.length; q += 4) {
    for (let e = 0; e < 4; e++) {
      const a = quads[q + e]!, b = quads[q + ((e + 1) % 4)]!;
      neighbors[a]!.push(b);
      neighbors[b]!.push(a);
    }
  }
  const pos = new Float32Array(positions);
  const next = new Float32Array(pos.length);
  for (let it = 0; it < iterations; it++) {
    for (let v = 0; v < vcount; v++) {
      const nb = neighbors[v]!;
      if (nb.length === 0) { next.set(pos.subarray(v * 3, v * 3 + 3), v * 3); continue; }
      let x = 0, y = 0, z = 0;
      for (const u of nb) { x += pos[u * 3]!; y += pos[u * 3 + 1]!; z += pos[u * 3 + 2]!; }
      const inv = (1 - keep) / nb.length;
      next[v * 3] = pos[v * 3]! * keep + x * inv;
      next[v * 3 + 1] = pos[v * 3 + 1]! * keep + y * inv;
      next[v * 3 + 2] = pos[v * 3 + 2]! * keep + z * inv;
    }
    pos.set(next);
  }
  for (let i = 0; i < pos.length; i++) positions[i] = pos[i]!;
}

/** 사각형 → 삼각형 인덱스. */
export function quadsToTriangles(quads: number[]): number[] {
  const tri: number[] = [];
  for (let q = 0; q < quads.length; q += 4) {
    const a = quads[q]!, b = quads[q + 1]!, c = quads[q + 2]!, d = quads[q + 3]!;
    tri.push(a, b, c, a, c, d);
  }
  return tri;
}

/**
 * 섬 제거 — 가장 큰 연결 성분의 `minShare` 미만인 조각을 버린다.
 *
 * 생성 이미지의 뷰 불일치는 가는 부위(발목·손목)를 조각내 공중에 띄운다(실측: 고블린 8뷰).
 * 그 조각은 텍스처도 리깅도 못 받는 쓰레기다. 진짜 분리된 부품(무기 등)이 이 문턱보다
 * 작을 수 있으니 문턱은 낮게 둔다.
 */
export function dropIslands(
  positions: number[],
  quads: number[],
  minShare = 0.01,
): { positions: number[]; quads: number[]; dropped: number } {
  const n = positions.length / 3;
  const parent = new Int32Array(n);
  for (let i = 0; i < n; i++) parent[i] = i;
  const find = (a: number): number => { while (parent[a] !== a) { parent[a] = parent[parent[a]!]!; a = parent[a]!; } return a; };
  const union = (a: number, b: number) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };
  for (let q = 0; q < quads.length; q += 4) {
    union(quads[q]!, quads[q + 1]!); union(quads[q + 1]!, quads[q + 2]!); union(quads[q + 2]!, quads[q + 3]!);
  }
  const count = new Map<number, number>();
  for (let q = 0; q < quads.length; q += 4) { const r = find(quads[q]!); count.set(r, (count.get(r) ?? 0) + 1); }
  let largest = 0;
  for (const c of count.values()) largest = Math.max(largest, c);
  const keepRoot = new Set<number>();
  for (const [r, c] of count) if (c >= Math.max(8, largest * minShare)) keepRoot.add(r);
  if (keepRoot.size === count.size) return { positions, quads, dropped: 0 };
  const remap = new Int32Array(n).fill(-1);
  const outPos: number[] = [];
  const outQuads: number[] = [];
  let dropped = 0;
  for (let q = 0; q < quads.length; q += 4) {
    if (!keepRoot.has(find(quads[q]!))) { dropped++; continue; }
    for (let k = 0; k < 4; k++) {
      const v = quads[q + k]!;
      if (remap[v] < 0) { remap[v] = outPos.length / 3; outPos.push(positions[v * 3]!, positions[v * 3 + 1]!, positions[v * 3 + 2]!); }
      outQuads.push(remap[v]!);
    }
  }
  return { positions: outPos, quads: outQuads, dropped };
}
